class LensSelectorFlow extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.dialog = this.querySelector('dialog-component');
    this.stepsContainer = this.querySelector('[data-steps-container]');
    this.reviewPanel = this.querySelector('[data-panel="review"]');
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.backButton = this.querySelector('[data-step-back]');
    this.errorEl = this.querySelector('[data-cart-error]');
    this.notesInput = this.querySelector('[data-notes-input]');
    this.reviewSummary = this.querySelector('[data-review-summary]');
    this.confirmButton = this.querySelector('[data-confirm-add-to-cart]');
    this.termsCheckbox = this.querySelector('[data-terms-checkbox]');
    this.triggerButton = this.querySelector('[data-lens-selector-open]');
    this.frameOnlyButton = this.querySelector('[data-buy-frame-only]');
    this.redirectToCheckout = this.dataset.redirectToCheckout === 'true';

    // Passos configurados pelo lojista no Editor de temas, na ordem em que foram montados.
    this.stepEls = Array.from(this.stepsContainer.querySelectorAll(':scope > [data-step]'));

    this.frame = {
      variantId: this.dataset.frameVariantId,
      title: this.dataset.frameTitle,
      color: this.dataset.frameColor,
      priceCents: parseInt(this.dataset.framePriceCents, 10) || 0,
      available: this.dataset.frameAvailable === 'true',
    };

    this.updateTriggerAvailability();

    this.resetState();

    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('submit', (event) => event.preventDefault());

    document.addEventListener('variant:update', this.handleVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.handleVariantChange);
  }

  handleVariantChange = (event) => {
    const variant = event.detail && event.detail.resource;
    if (!variant) return;

    const section = this.closest('.shopify-section');
    if (section && event.target instanceof Node && !section.contains(event.target)) return;

    this.frame.variantId = variant.id;
    this.frame.color = variant.title || variant.public_title || variant.option1;
    this.frame.available = variant.available !== false;
    if (typeof variant.price === 'number') this.frame.priceCents = variant.price;
    this.updateTriggerAvailability();
  };

  updateTriggerAvailability() {
    if (this.triggerButton) {
      if (this.frame.available) {
        this.triggerButton.disabled = false;
        this.triggerButton.textContent = this.triggerButton.dataset.ctaLabel || this.triggerButton.textContent;
      } else {
        this.triggerButton.disabled = true;
        this.triggerButton.textContent = this.triggerButton.dataset.soldOutLabel || 'Produto esgotado';
      }
    }

    if (this.frameOnlyButton) {
      if (this.frame.available) {
        this.frameOnlyButton.disabled = false;
        this.frameOnlyButton.textContent = this.frameOnlyButton.dataset.ctaLabel || this.frameOnlyButton.textContent;
      } else {
        this.frameOnlyButton.disabled = true;
        this.frameOnlyButton.textContent = this.frameOnlyButton.dataset.soldOutLabel || 'Produto esgotado';
      }
    }

    if (!this.frame.available) {
      // Se a variação ficar esgotada com o modal já aberto (troca de cor em outra aba,
      // por exemplo), fecha o fluxo pra não deixar o cliente confirmar um pedido
      // impossível de cumprir.
      this.dialog?.closeDialog();
    }
  }

  resetState() {
    this.state = {
      stepIndex: -1,
      subStep: 'groups',
      reviewShown: false,
      selections: [],
      notes: '',
      receitaMode: null,
      receitaFile: null,
    };
    this.currentGroupRequiresPrescription = false;
    this.addingFrameOnly = false;
    this.currentGroupId = null;
    this.currentStepId = null;
    this.pendingAddons = new Map();

    if (this.notesInput) this.notesInput.value = '';
    this.stepEls.forEach((el) => {
      el.hidden = true;
      el.querySelectorAll('[data-receita-file-input]').forEach((input) => {
        input.value = '';
      });
      el.querySelectorAll('[data-lens-addon]').forEach((btn) => {
        btn.hidden = true;
        btn.setAttribute('aria-pressed', 'false');
        btn.classList.remove('is-selected');
        const labelEl = btn.querySelector('[data-addon-toggle-text]');
        if (labelEl && labelEl.dataset.baseLabel) labelEl.textContent = labelEl.dataset.baseLabel;
      });
      el.querySelectorAll('[data-group-addons-intro], [data-addons-continue]').forEach((addonEl) => {
        addonEl.hidden = true;
      });
      el.querySelectorAll('[data-lens-option], [data-tier]').forEach((baseEl) => {
        baseEl.hidden = false;
      });
    });
    if (this.reviewPanel) this.reviewPanel.hidden = true;

    if (this.termsCheckbox) {
      this.termsCheckbox.checked = false;
      if (this.confirmButton) this.confirmButton.disabled = true;
    }
  }

  handleClick = (event) => {
    if (event.target.closest('[data-lens-selector-open]')) {
      if (!this.frame.available) return;
      this.resetState();
      this.dialog?.showDialog();
      this.goToFirstStep();
      return;
    }

    if (event.target.closest('[data-buy-frame-only]')) {
      const btn = event.target.closest('[data-buy-frame-only]');
      if (!this.frame.available) return;
      this.addFrameOnlyToCart(btn);
      return;
    }

    if (event.target.closest('[data-step-back]')) {
      this.goBack();
      return;
    }

    const groupCard = event.target.closest('[data-group]');
    if (groupCard) {
      this.selectGroup(groupCard);
      return;
    }

    const addonToggle = event.target.closest('[data-lens-addon]');
    if (addonToggle) {
      this.toggleAddonCard(addonToggle);
      return;
    }

    if (event.target.closest('[data-addons-continue]')) {
      this.confirmAddons();
      return;
    }

    const lensCard = event.target.closest('[data-lens-option], [data-color-option]');
    if (lensCard) {
      this.selectAddon(lensCard);
      return;
    }

    const receitaCard = event.target.closest('[data-receita-mode]');
    if (receitaCard) {
      this.selectReceitaMode(receitaCard, receitaCard.dataset.receitaMode);
      return;
    }

    if (event.target.closest('[data-confirm-add-to-cart]')) {
      this.submitToCart();
    }
  };

  handleChange = (event) => {
    if (event.target.matches('[data-receita-file-input]')) {
      this.state.receitaFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      if (this.state.receitaFile) this.advance();
      return;
    }

    if (event.target === this.notesInput) {
      this.state.notes = this.notesInput.value;
      return;
    }

    if (event.target === this.termsCheckbox) {
      if (this.confirmButton) this.confirmButton.disabled = !this.termsCheckbox.checked;
    }
  };

  goToFirstStep() {
    if (this.stepEls.length === 0) {
      this.showReview();
      return;
    }
    this.showStep(0);
  }

  showStep(index) {
    this.stepEls.forEach((el, i) => {
      el.hidden = i !== index;
    });
    if (this.reviewPanel) this.reviewPanel.hidden = true;
    this.state.stepIndex = index;
    this.state.reviewShown = false;

    const stepEl = this.stepEls[index];
    if (stepEl?.dataset.stepType === 'lens-type') {
      this.showGroupCards(stepEl);
    } else {
      this.updateProgress();
    }
  }

  showGroupCards(stepEl) {
    stepEl.querySelectorAll('[data-group]').forEach((el) => {
      el.hidden = false;
    });
    stepEl.querySelectorAll('[data-group-lenses]').forEach((el) => {
      el.hidden = true;
    });
    this.state.subStep = 'groups';
    this.updateProgress();
  }

  showGroupLensesOnly(stepEl) {
    const lensesEl = stepEl.querySelector(`[data-group-lenses][data-group-id="${this.currentGroupId}"]`);
    if (lensesEl) {
      lensesEl.querySelectorAll('[data-lens-option], [data-tier]').forEach((el) => {
        el.hidden = false;
      });
      lensesEl.querySelectorAll('[data-lens-addon], [data-group-addons-intro], [data-addons-continue]').forEach((el) => {
        el.hidden = true;
      });
    }
    this.state.subStep = 'lenses';
    this.updateProgress();
  }

  selectGroup(groupCard) {
    const stepEl = groupCard.closest('[data-step]');
    if (!stepEl) return;

    if (groupCard.dataset.noLensNeeded === 'true') {
      // Ex: "Somente armação" — sem lente pra escolher e sem receita/termos
      // envolvidos, então adiciona a armação direto ao carrinho (ou já manda
      // pro checkout, se essa opção estiver ativa).
      this.currentGroupRequiresPrescription = false;
      this.addFrameOnlyToCart(groupCard);
      return;
    }

    stepEl.querySelectorAll('[data-group]').forEach((el) => {
      el.hidden = true;
    });
    stepEl.querySelectorAll('[data-group-lenses]').forEach((el) => {
      el.hidden = true;
    });

    const groupId = groupCard.dataset.groupId;
    const lensesEl = stepEl.querySelector(`[data-group-lenses][data-group-id="${groupId}"]`);
    if (lensesEl) lensesEl.hidden = false;

    this.currentGroupId = groupId;
    this.currentStepId = stepEl.dataset.stepId;
    this.currentGroupRequiresPrescription = groupCard.dataset.requiresPrescription === 'true';
    this.state.subStep = 'lenses';
    this.updateProgress();
  }

  selectAddon(cardEl) {
    const stepEl = cardEl.closest('[data-step]');
    const requiresPrescription = this.currentGroupRequiresPrescription;

    const selection = {
      stepId: stepEl?.dataset.stepId,
      variantId: cardEl.dataset.lensVariantId,
      title: cardEl.dataset.lensTitle,
      priceCents: parseInt(cardEl.dataset.lensPriceCents, 10) || 0,
      requiresPrescription,
    };

    // Se o cliente reabrir este passo (ex: apertou "Voltar" pra conferir algo) e
    // clicar de novo na MESMA lente, não mexe na seleção nem nos aditivos já
    // escolhidos — só troca/limpa os aditivos quando a lente realmente muda.
    const existingCore = this.state.selections.find((s) => s.stepId === selection.stepId);
    const isSameLens = existingCore && String(existingCore.variantId) === String(selection.variantId);

    if (!isSameLens) {
      this.state.selections = this.state.selections.filter(
        (s) => s.stepId !== selection.stepId && !(s.stepId && s.stepId.startsWith(`${selection.stepId}::addon::`))
      );
      this.state.selections.push(selection);
    }
    this.currentGroupRequiresPrescription = false;

    const lensesEl = cardEl.closest('[data-group-lenses]');
    const hasAddons = lensesEl && lensesEl.querySelector('[data-lens-addon]');
    if (hasAddons) {
      this.showGroupAddons(lensesEl);
      return;
    }

    this.advance();
  }

  showGroupAddons(lensesEl) {
    lensesEl.querySelectorAll('[data-lens-option], [data-tier]').forEach((el) => {
      el.hidden = true;
    });

    // Reconstrói os aditivos pendentes a partir do que já está confirmado em
    // state.selections pra este passo, em vez de resetar tudo pra "não
    // selecionado" — evita perder silenciosamente uma escolha já feita quando o
    // cliente navega de volta pra essa etapa (ex: aperta "Voltar" e reconfirma).
    this.pendingAddons = new Map();
    this.state.selections
      .filter((s) => s.stepId && s.stepId.startsWith(`${this.currentStepId}::addon::`))
      .forEach((s) => this.pendingAddons.set(String(s.variantId), s));

    lensesEl.querySelectorAll('[data-lens-addon]').forEach((btn) => {
      const isPreselected = this.pendingAddons.has(String(btn.dataset.addonVariantId));
      btn.hidden = false;
      btn.setAttribute('aria-pressed', String(isPreselected));
      btn.classList.toggle('is-selected', isPreselected);
      const labelEl = btn.querySelector('[data-addon-toggle-text]');
      if (labelEl) {
        if (!labelEl.dataset.baseLabel) labelEl.dataset.baseLabel = labelEl.textContent;
        labelEl.textContent = isPreselected ? 'Adicionado ✓' : labelEl.dataset.baseLabel;
      }
    });
    lensesEl.querySelectorAll('[data-group-addons-intro], [data-addons-continue]').forEach((el) => {
      el.hidden = false;
    });

    this.state.subStep = 'addons';
    this.updateProgress();
  }

  toggleAddonCard(cardEl) {
    const isSelected = cardEl.getAttribute('aria-pressed') === 'true';
    const nextSelected = !isSelected;
    cardEl.setAttribute('aria-pressed', String(nextSelected));
    cardEl.classList.toggle('is-selected', nextSelected);

    const labelEl = cardEl.querySelector('[data-addon-toggle-text]');
    if (labelEl) {
      if (!labelEl.dataset.baseLabel) labelEl.dataset.baseLabel = labelEl.textContent;
      labelEl.textContent = nextSelected ? 'Adicionado ✓' : labelEl.dataset.baseLabel;
    }

    const variantId = cardEl.dataset.addonVariantId;
    if (!nextSelected) {
      this.pendingAddons.delete(variantId);
    } else {
      this.pendingAddons.set(variantId, {
        stepId: `${this.currentStepId}::addon::${variantId}`,
        variantId,
        title: cardEl.dataset.addonTitle,
        priceCents: parseInt(cardEl.dataset.addonPriceCents, 10) || 0,
        requiresPrescription: false,
      });
    }
  }

  confirmAddons() {
    this.state.selections = this.state.selections.filter(
      (s) => !(s.stepId && s.stepId.startsWith(`${this.currentStepId}::addon::`))
    );
    this.state.selections.push(...this.pendingAddons.values());
    this.advance();
  }

  selectReceitaMode(cardEl, mode) {
    this.state.receitaMode = mode;

    if (mode === 'upload') {
      const stepEl = cardEl.closest('[data-step]');
      const fileInput = stepEl?.querySelector('[data-receita-file-input]');
      fileInput?.click();
      return;
    }

    this.state.receitaFile = null;
    this.advance();
  }

  currentRequiresPrescription() {
    return this.state.selections.some((s) => s.requiresPrescription);
  }

  advance() {
    let next = this.state.stepIndex + 1;
    while (next < this.stepEls.length) {
      const stepType = this.stepEls[next].dataset.stepType;
      if (stepType === 'prescription' && !this.currentRequiresPrescription()) {
        next += 1;
        continue;
      }
      this.showStep(next);
      return;
    }
    this.showReview();
  }

  previousVisibleStepIndex(fromIndex) {
    let idx = fromIndex;
    while (idx >= 0) {
      const stepType = this.stepEls[idx].dataset.stepType;
      if (stepType === 'prescription' && !this.currentRequiresPrescription()) {
        idx -= 1;
        continue;
      }
      return idx;
    }
    return -1;
  }

  goBack() {
    const currentStepEl = this.stepEls[this.state.stepIndex];

    if (!this.state.reviewShown && currentStepEl?.dataset.stepType === 'lens-type') {
      if (this.state.subStep === 'addons') {
        this.showGroupLensesOnly(currentStepEl);
        return;
      }
      if (this.state.subStep === 'lenses') {
        this.showGroupCards(currentStepEl);
        return;
      }
    }

    const fromIndex = this.state.reviewShown ? this.stepEls.length - 1 : this.state.stepIndex - 1;
    const targetIndex = this.previousVisibleStepIndex(fromIndex);

    if (targetIndex < 0) {
      this.dialog?.closeDialog();
      return;
    }

    this.showStep(targetIndex);
  }

  showReview() {
    this.stepEls.forEach((el) => {
      el.hidden = true;
    });
    if (this.reviewPanel) this.reviewPanel.hidden = false;
    this.state.reviewShown = true;
    this.renderReview();
    this.updateProgress();
  }

  renderReview() {
    if (!this.reviewSummary) return;

    const frameItem = `
      <div class="lens-selector__review-item">
        <dl>
          <dt>Armação${this.frame.color ? ' - ' + this.frame.color : ''}</dt>
          <dd>${this.formatMoney(this.frame.priceCents)}</dd>
        </dl>
      </div>
    `;

    const addonItems = this.state.selections
      .map((selection) => {
        const receitaText = selection.requiresPrescription
          ? this.state.receitaMode === 'upload'
            ? 'Enviar foto da receita'
            : 'Enviar depois'
          : null;

        return `
          <div class="lens-selector__review-item">
            <dl>
              <dt>${selection.title}</dt>
              <dd>${this.formatMoney(selection.priceCents)}</dd>
            </dl>
            ${receitaText ? `<p class="lens-selector__review-meta">Forma de envio da receita: ${receitaText}</p>` : ''}
          </div>
        `;
      })
      .join('');

    const totalCents = this.frame.priceCents + this.state.selections.reduce((sum, s) => sum + (s.priceCents || 0), 0);
    const totalItem = `
      <div class="lens-selector__review-item lens-selector__review-total">
        <dl>
          <dt>Total</dt>
          <dd>${this.formatMoney(totalCents)}</dd>
        </dl>
      </div>
    `;

    this.reviewSummary.innerHTML = frameItem + addonItems + totalItem;
  }

  formatMoney(cents) {
    if (!cents) return 'Grátis';
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  updateProgress() {
    const total = this.stepEls.length + 1; // +1 pro passo de revisão
    let current = this.state.reviewShown ? total : this.state.stepIndex + 1;
    if (!this.state.reviewShown && this.state.subStep === 'addons') current += 0.4;
    const ratio = Math.min(current / total, 1);

    // scaleX é acelerado por composição (GPU) — animar width forçaria layout a cada frame.
    if (this.progressFill) this.progressFill.style.transform = `scaleX(${ratio})`;

    const atVeryStart = !this.state.reviewShown && this.state.stepIndex <= 0 && this.state.subStep === 'groups';
    if (this.backButton) this.backButton.hidden = atVeryStart;
    if (this.errorEl) this.errorEl.hidden = true;
  }

  goToCartOrCheckout() {
    if (!this.redirectToCheckout) {
      window.location.href = '/cart';
      return;
    }

    // Se a Yampi estiver ativa (snippets/YampiSnippet.liquid carregado), ela expõe
    // a função global getNewCheckoutURL(), que lê /cart.json, consulta a API dela
    // e redireciona pra URL de pagamento correta — nesse caso usamos ela, já que
    // o /checkout nativo não é interceptado pela Yampi. Quando a Yampi está
    // desativada (testando o checkout nativo do Shopify), essa função não existe
    // mais, então vamos direto pro /checkout nativo em vez de cair de volta no
    // /cart (o que fazia o botão "pular carrinho" parecer quebrado).
    if (typeof window.getNewCheckoutURL === 'function') {
      window.getNewCheckoutURL();
    } else {
      window.location.href = '/checkout';
    }
  }

  async addFrameOnlyToCart(triggerEl) {
    if (this.addingFrameOnly || !this.frame.available) return;
    this.addingFrameOnly = true;
    if (this.errorEl) this.errorEl.hidden = true;
    if (triggerEl) triggerEl.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: this.frame.variantId,
          quantity: 1,
          properties: {
            Armação: this.frame.title,
          },
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(errorBody?.description || 'Não foi possível adicionar a armação ao carrinho.');
      }

      this.goToCartOrCheckout();
    } catch (error) {
      this.addingFrameOnly = false;
      if (triggerEl) triggerEl.removeAttribute('aria-busy');
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent =
          error instanceof Error ? error.message : 'Não foi possível adicionar ao carrinho. Tente novamente.';
      }
    }
  }

  async submitToCart() {
    if (!this.confirmButton || !this.frame.available) return;

    const originalLabel = this.confirmButton.textContent;
    const loadingLabel = this.redirectToCheckout
      ? this.confirmButton.dataset.confirmCheckoutLoadingLabel || 'Preparando checkout...'
      : this.confirmButton.dataset.confirmLoadingLabel || 'Adicionando...';
    this.confirmButton.disabled = true;
    this.confirmButton.textContent = loadingLabel;
    if (this.errorEl) this.errorEl.hidden = true;

    const bundleId =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
      const frameResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: this.frame.variantId,
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            Armação: this.frame.title,
          },
        }),
      });

      if (!frameResponse.ok) {
        const errorBody = await frameResponse.json().catch(() => null);
        throw new Error(errorBody?.description || 'Não foi possível adicionar a armação ao carrinho.');
      }

      for (const selection of this.state.selections) {
        const formData = new FormData();
        formData.append('id', String(selection.variantId));
        formData.append('quantity', '1');
        formData.append('properties[_bundle_id]', bundleId);
        formData.append('properties[Item selecionado]', selection.title);

        if (selection.requiresPrescription) {
          formData.append(
            'properties[Forma de envio da receita]',
            this.state.receitaMode === 'upload' ? 'Enviar agora' : 'Enviar depois'
          );
          if (this.state.receitaFile) {
            formData.append('properties[Receita]', this.state.receitaFile, this.state.receitaFile.name);
          }
        }

        if (this.state.notes) {
          formData.append('properties[Observações do pedido]', this.state.notes);
        }

        const addonResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        if (!addonResponse.ok) {
          const errorBody = await addonResponse.json().catch(() => null);
          throw new Error(errorBody?.description || 'Não foi possível adicionar a lente ao carrinho.');
        }
      }

      this.goToCartOrCheckout();
    } catch (error) {
      this.confirmButton.disabled = false;
      this.confirmButton.textContent = originalLabel;
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent =
          error instanceof Error ? error.message : 'Não foi possível adicionar ao carrinho. Tente novamente.';
      }
    }
  }
}

if (!customElements.get('lens-selector-flow')) {
  customElements.define('lens-selector-flow', LensSelectorFlow);
}
